import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Lock, Palette, Image, FileText, Code } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

export default function Whitelabel() {
  const [settings, setSettings] = useState({
    is_enabled: false,
    custom_brand_name: '',
    custom_logo_url: '',
    custom_favicon_url: '',
    hide_powered_by: false,
    custom_footer_text: '',
    custom_css: ''
  });
  const [whitelabelAllowed, setWhitelabelAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/tenant/whitelabel');
      setSettings(res.data.settings || {});
      setWhitelabelAllowed(res.data.whitelabel_allowed);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat pengaturan', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!whitelabelAllowed) {
      toast({ title: 'Error', description: 'Fitur whitelabel tidak tersedia di paket Anda', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.put('/tenant/whitelabel', settings);
      toast({ title: 'Berhasil', description: 'Pengaturan whitelabel disimpan' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menyimpan', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="whitelabel-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Whitelabel</h1>
          <p className="text-muted-foreground">Kustomisasi branding toko Anda</p>
        </div>
        {!whitelabelAllowed && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Lock className="h-3 w-3" /> Premium Feature
          </Badge>
        )}
      </div>

      {!whitelabelAllowed && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Fitur Premium</p>
                <p className="text-sm text-amber-700">Upgrade ke paket Premium untuk mengakses fitur Whitelabel</p>
              </div>
              <Button size="sm" className="ml-auto">Upgrade</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={!whitelabelAllowed ? 'opacity-50 pointer-events-none' : ''}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Aktifkan Whitelabel</CardTitle>
                <CardDescription>Aktifkan untuk menggunakan branding kustom</CardDescription>
              </div>
              <Switch checked={settings.is_enabled} onCheckedChange={(v) => setSettings({ ...settings, is_enabled: v })} />
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" /> Branding
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nama Brand Kustom</Label>
                <Input value={settings.custom_brand_name} onChange={(e) => setSettings({ ...settings, custom_brand_name: e.target.value })} placeholder="Nama Toko Anda" />
              </div>
              <div>
                <Label>Footer Text</Label>
                <Input value={settings.custom_footer_text} onChange={(e) => setSettings({ ...settings, custom_footer_text: e.target.value })} placeholder="© 2024 Nama Toko Anda" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={settings.hide_powered_by} onCheckedChange={(v) => setSettings({ ...settings, hide_powered_by: v })} />
                <Label>Sembunyikan "Powered by Tokomoe"</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" /> Logo & Favicon
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>URL Logo Kustom</Label>
                <Input value={settings.custom_logo_url} onChange={(e) => setSettings({ ...settings, custom_logo_url: e.target.value })} placeholder="https://example.com/logo.png" />
              </div>
              <div>
                <Label>URL Favicon Kustom</Label>
                <Input value={settings.custom_favicon_url} onChange={(e) => setSettings({ ...settings, custom_favicon_url: e.target.value })} placeholder="https://example.com/favicon.ico" />
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" /> Custom CSS
              </CardTitle>
              <CardDescription>Tambahkan CSS kustom untuk styling lebih lanjut</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={settings.custom_css} 
                onChange={(e) => setSettings({ ...settings, custom_css: e.target.value })} 
                placeholder=".my-class { color: red; }"
                className="font-mono text-sm"
                rows={6}
              />
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || !whitelabelAllowed}>
            {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
          </Button>
        </div>
      </div>
    </div>
  );
}
